import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Input, Card } from '@atoms/index';
import {
  AddAddressModal,
  OrderSummary,
  GuestContactForm,
  GuestDeliveryAddressSection,
  createEmptyGuestDeliveryAddress,
  isValidDeliveryCoordinate,
} from '@organisms/index';
import type { GuestContactData, GuestDeliveryAddress } from '@organisms/index';
import { useCartStore } from '@store/cartStore';
import { useAuthStore } from '@store/authStore';
import { orderService } from '@services/orderService';
import { addressService } from '@services/addressService';
import { geoService } from '@services/geoService';
import { paymentService } from '@services/paymentService';
import { authService } from '@services/authService';
import { catalogService } from '@services/catalogService';
import type { UserAddress, CreateOrderDTO } from '@/types';
import {
  formatPhoneE164,
  formatTurkishMobileForDisplay,
  getTurkishMobileValidationError,
  isValidTurkishMobilePhone,
} from '@utils/format';

function splitFullName(fullName: string): { name: string; surname: string } {
  const parts = fullName.trim().split(/\s+/);
  const name = parts[0] || '';
  const surname = parts.slice(1).join(' ') || name;
  return { name, surname };
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, updateProfile, refreshUser } = useAuthStore();
  const {
    items,
    restaurantId,
    serviceZoneId,
    restaurant,
    subtotal,
    deliveryFee,
    serviceFee,
    promotionDiscount,
    appliedPromotionName,
    total,
    clearCart,
    syncFromBackend,
  } = useCartStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressZoneWarning, setAddressZoneWarning] = useState<string | null>(null);

  // Address selection
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  // Delivery time selection
  const [deliveryMode, setDeliveryMode] = useState<'now' | 'scheduled'>('now');
  const [desiredDeliveryTime, setDesiredDeliveryTime] = useState('');

  const [paymentMethod, setPaymentMethod] = useState<'CREDIT_CARD' | 'CASH'>('CREDIT_CARD');

  const [notes, setNotes] = useState('');
  const [restaurantOpen, setRestaurantOpen] = useState<boolean | null>(null);

  const [guestContact, setGuestContact] = useState<GuestContactData>({
    fullName: '',
    email: '',
    phone: '',
  });
  const [guestAddress, setGuestAddress] = useState<GuestDeliveryAddress>(
    createEmptyGuestDeliveryAddress(),
  );
  const [checkoutPhone, setCheckoutPhone] = useState('');
  const [savePhoneToProfile, setSavePhoneToProfile] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser().catch((err) => {
        console.error('Failed to refresh user profile:', err);
      });
    }
  }, [isAuthenticated, refreshUser]);

  useEffect(() => {
    if (user?.phone?.trim() && isValidTurkishMobilePhone(user.phone)) {
      setCheckoutPhone(formatTurkishMobileForDisplay(user.phone));
      setSavePhoneToProfile(true);
    }
  }, [user?.phone]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAddresses();
    }
    syncFromBackend().catch((err) => {
      console.error('Failed to sync cart from backend:', err);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!restaurantId) {
      setRestaurantOpen(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await catalogService.getRestaurant(restaurantId);
        if (!cancelled) {
          setRestaurantOpen(r.isOpen === true);
        }
      } catch {
        if (!cancelled) {
          setRestaurantOpen(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  // Validate address zone when address or zone context changes
  useEffect(() => {
    if (selectedAddressId && (serviceZoneId || restaurantId)) {
      validateAddressZone(selectedAddressId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddressId, serviceZoneId, restaurantId]);

  const loadAddresses = async () => {
    try {
      const data = await addressService.getAddresses();
      setAddresses(data);
      
      // Select default address if available
      const defaultAddress = data.find((addr) => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else if (data.length > 0) {
        setSelectedAddressId(data[0].id);
      }
    } catch (err) {
      console.error('Load addresses error:', err);
    }
  };

  const handleAddressAdded = () => {
    loadAddresses();
    setIsAddressModalOpen(false);
  };

  const validateAddressZone = async (addressId: string) => {
    try {
      const address = await addressService.getAddress(addressId);

      if (!address.location) {
        setAddressZoneWarning('Adres konum bilgisi bulunamadı.');
        return;
      }

      const location = address.location as { type: string; coordinates: number[] };
      const [longitude, latitude] = location.coordinates;

      if (restaurantId) {
        const restaurantZone = await geoService.checkRestaurantDeliveryZone(
          restaurantId,
          latitude,
          longitude,
        );
        if (restaurantZone) {
          setAddressZoneWarning(null);
          setError(null);
        } else {
          setAddressZoneWarning(
            'Bu adres restoranın teslimat bölgesi dışında. Lütfen restoranın hizmet verdiği bir adres seçin.'
          );
          setError('Seçilen adres restoranın teslimat bölgesi dışında.');
        }
        return;
      }

      if (!serviceZoneId) {
        setAddressZoneWarning(
          'Bu adres hizmet bölgesi dışında. Lütfen hizmet bölgesi içindeki bir adres seçin.'
        );
        setError('Seçilen adres hizmet bölgesi dışında. Lütfen başka bir adres seçin.');
        return;
      }

      const zone = await geoService.checkServiceZone(latitude, longitude);

      if (!zone) {
        setAddressZoneWarning(
          'Bu adres hizmet bölgesi dışında. Lütfen hizmet bölgesi içindeki bir adres seçin.'
        );
        setError('Seçilen adres hizmet bölgesi dışında. Lütfen başka bir adres seçin.');
      } else if (zone.id !== serviceZoneId) {
        setAddressZoneWarning(
          `Bu adres farklı bir hizmet bölgesinde (${zone.district}, ${zone.city}). ` +
            `Sepetinizdeki restoranlar bu bölgede hizmet vermiyor. ` +
            `Lütfen doğru adresi seçin veya ana sayfaya dönüp bu bölge için restoranları görüntüleyin.`
        );
        setError('Seçilen adres sepetteki restoranların hizmet bölgesi dışında.');
      } else {
        setAddressZoneWarning(null);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to validate address zone:', err);
      setAddressZoneWarning('Adres kontrolü yapılırken bir hata oluştu.');
    }
  };

  const validateGuestDeliveryZone = async (
    latitude: number,
    longitude: number,
  ): Promise<boolean> => {
    if (restaurantId) {
      const restaurantZone = await geoService.checkRestaurantDeliveryZone(
        restaurantId,
        latitude,
        longitude,
      );
      if (!restaurantZone) {
        setAddressZoneWarning(
          'Bu adres restoranın teslimat bölgesi dışında. Lütfen restoranın hizmet verdiği bir adres seçin.',
        );
        setError('Seçilen adres restoranın teslimat bölgesi dışında.');
        return false;
      }
      setAddressZoneWarning(null);
      setError(null);
      return true;
    }

    if (!serviceZoneId) {
      setError('Seçilen adres hizmet bölgesi dışında.');
      return false;
    }

    const zone = await geoService.checkServiceZone(latitude, longitude);
    if (!zone || zone.id !== serviceZoneId) {
      setError('Seçilen adres sepetteki restoranların hizmet bölgesi dışında.');
      return false;
    }

    setAddressZoneWarning(null);
    setError(null);
    return true;
  };

  const buildGuestDeliveryAddressText = (): string => {
    const parts = [guestAddress.fullAddress];
    if (guestAddress.buildingInfo) {
      parts.push(guestAddress.buildingInfo);
    }
    if (guestAddress.district) {
      parts.push(guestAddress.district);
    }
    if (guestAddress.city) {
      parts.push(guestAddress.city);
    }
    return parts.filter(Boolean).join(', ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAddressZoneWarning(null);

    if (restaurantOpen === false) {
      setError('Restoran şu anda kapalı. Sipariş veremezsiniz.');
      return;
    }

    if (!restaurantId) {
      setError('Eksik sipariş bilgisi.');
      return;
    }

    if (!isAuthenticated) {
      if (!guestContact.fullName.trim() || !guestContact.phone.trim() || !guestContact.email.trim()) {
        setError('Lütfen ad soyad, e-posta ve telefon numaranızı girin.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestContact.email.trim())) {
        setError('Lütfen geçerli bir e-posta adresi girin.');
        return;
      }
      if (
        !guestAddress.fullAddress.trim() ||
        !isValidDeliveryCoordinate(guestAddress.latitude, guestAddress.longitude)
      ) {
        setError('Lütfen adres arayın veya haritadan teslimat noktanızı seçin.');
        return;
      }

      try {
        const zoneValid = await validateGuestDeliveryZone(
          guestAddress.latitude,
          guestAddress.longitude,
        );
        if (!zoneValid) {
          return;
        }
      } catch {
        setError('Adres kontrolü yapılırken bir hata oluştu. Lütfen tekrar deneyin.');
        return;
      }
    } else if (!selectedAddressId) {
      setError('Lütfen bir teslimat adresi seçin.');
      return;
    }

    if (isAuthenticated) {
      const phoneError = getTurkishMobileValidationError(checkoutPhone);
      if (phoneError) {
        setError(phoneError);
        return;
      }
    }

    if (isAuthenticated && selectedAddressId) {
      try {
        const address = await addressService.getAddress(selectedAddressId);
        if (address.location) {
          const location = address.location as { type: string; coordinates: number[] };
          const [longitude, latitude] = location.coordinates;

          if (restaurantId) {
            const restaurantZone = await geoService.checkRestaurantDeliveryZone(
              restaurantId,
              latitude,
              longitude,
            );
            if (!restaurantZone) {
              setError(
                'Seçilen adres restoranın teslimat bölgesi dışında. Lütfen restoranın hizmet verdiği bir adres seçin.'
              );
              return;
            }
          } else if (serviceZoneId) {
            const zone = await geoService.checkServiceZone(latitude, longitude);
            if (!zone || zone.id !== serviceZoneId) {
              setError(
                'Seçilen adres sepetteki restoranların hizmet bölgesi dışında. ' +
                  'Lütfen doğru adresi seçin veya ana sayfaya dönüp bu bölge için restoranları görüntüleyin.'
              );
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to validate address zone before submit:', err);
        setError('Adres kontrolü yapılırken bir hata oluştu. Lütfen tekrar deneyin.');
        return;
      }
    }

    if (deliveryMode === 'scheduled' && !desiredDeliveryTime) {
      setError('Lütfen teslimat tarihi ve saatini seçin.');
      return;
    }

    if (deliveryMode === 'scheduled') {
      const selectedTime = new Date(desiredDeliveryTime);
      if (selectedTime <= new Date()) {
        setError('Teslimat tarihi ve saati gelecekte olmalıdır.');
        return;
      }
    }

    setIsSubmitting(true);

    const desiredDeliveryTimeIso =
      deliveryMode === 'scheduled' && desiredDeliveryTime
        ? new Date(desiredDeliveryTime).toISOString()
        : undefined;

    const persistCheckoutPhoneIfRequested = async () => {
      if (!isAuthenticated || !savePhoneToProfile) {
        return;
      }
      await updateProfile({ phone: formatPhoneE164(checkoutPhone) });
    };

    const authenticatedCheckoutPhone = isAuthenticated
      ? formatPhoneE164(checkoutPhone)
      : '';

    try {
      if (!isAuthenticated) {
        const deliveryAddressText = buildGuestDeliveryAddressText();
        const { name: buyerName, surname: buyerSurname } = splitFullName(guestContact.fullName);
        const gsmNumber = formatPhoneE164(guestContact.phone);
        const zipCodeMatch = deliveryAddressText.match(/\b\d{5}\b/);
        const zipCode = zipCodeMatch ? zipCodeMatch[0] : '01000';

        if (paymentMethod === 'CREDIT_CARD') {
          const paymentData = {
            currency: 'TRY',
            enabledInstallments: [1, 2, 3, 6, 9],
            customerFullName: guestContact.fullName.trim(),
            customerPhone: guestContact.phone.trim(),
            customerEmail: guestContact.email.trim(),
            buyer: {
              name: buyerName,
              surname: buyerSurname,
              email: guestContact.email.trim(),
              gsmNumber,
              registrationAddress: deliveryAddressText,
              city: guestAddress.city || 'Adana',
              country: 'TR',
              zipCode,
            },
            shippingAddress: {
              contactName: guestContact.fullName.trim(),
              city: guestAddress.city || 'Adana',
              country: 'TR',
              address: deliveryAddressText,
              zipCode,
            },
            billingAddress: {
              contactName: guestContact.fullName.trim(),
              city: guestAddress.city || 'Adana',
              country: 'TR',
              address: deliveryAddressText,
              zipCode,
            },
            saveCard: false,
            notes: notes || undefined,
            desiredDeliveryTime: desiredDeliveryTimeIso,
            deliveryLatitude: guestAddress.latitude,
            deliveryLongitude: guestAddress.longitude,
            deliveryAddressText,
          };

          const paymentResult = await paymentService.initializePayment(paymentData);
          window.location.href = paymentResult.paymentPageUrl;
          return;
        }

        const orderData: CreateOrderDTO = {
          deliveryLatitude: guestAddress.latitude,
          deliveryLongitude: guestAddress.longitude,
          deliveryAddressText,
          customerFullName: guestContact.fullName.trim(),
          customerPhone: guestContact.phone.trim(),
          customerEmail: guestContact.email.trim(),
          desiredDeliveryTime: desiredDeliveryTimeIso,
          notes: notes || undefined,
          paymentMethod: 'CASH',
        };

        const { order, trackingUrl } = await orderService.createOrder(orderData);
        await clearCart();
        navigate('/checkout/confirmation', {
          state: { order, trackingUrl },
        });
        return;
      }

      try {
        await persistCheckoutPhoneIfRequested();
      } catch {
        setError('Telefon numarası profilinize kaydedilemedi. Lütfen tekrar deneyin.');
        setIsSubmitting(false);
        return;
      }

      if (paymentMethod === 'CREDIT_CARD') {
        let user;
        try {
          user = await authService.getCurrentUser();
        } catch (err: any) {
          setError(
            'Kullanıcı bilgileriniz alınamadı. Lütfen tekrar giriş yapın veya "Kapıda Nakit" seçeneğini kullanın.'
          );
          setIsSubmitting(false);
          return;
        }
        
        const address = await addressService.getAddress(selectedAddressId);
        
        if (!user.email || !user.name) {
          setError(
            'Kredi kartı ile ödeme için e-posta ve ad bilgileriniz gereklidir. ' +
            'Lütfen profil bilgilerinizi tamamlayın veya "Kapıda Nakit" seçeneğini kullanın.'
          );
          setIsSubmitting(false);
          return;
        }

        if (
          user.email.endsWith('@guest.lezzetyolda.com') ||
          user.email.endsWith('@lezzetyolda.app')
        ) {
          setError(
            'Kredi kartı ile ödeme için geçerli bir e-posta adresi gereklidir. ' +
            'Lütfen profil bilgilerinizde e-posta adresinizi güncelleyin.'
          );
          setIsSubmitting(false);
          return;
        }

        const buyerName = user.name || '';
        const buyerSurname = user.surname || (user.name ? user.name.split(/\s+/).slice(1).join(' ') : '') || buyerName;

        const zipCodeMatch = address.fullAddress.match(/\b\d{5}\b/);
        const zipCode = zipCodeMatch ? zipCodeMatch[0] : '01000';

        const paymentData = {
          currency: 'TRY',
          enabledInstallments: [1, 2, 3, 6, 9],
          buyer: {
            name: buyerName,
            surname: buyerSurname,
            email: user.email,
            gsmNumber: authenticatedCheckoutPhone,
            registrationAddress: address.fullAddress,
            city: address.city,
            country: 'TR',
            zipCode: zipCode,
          },
          shippingAddress: {
            contactName: `${buyerName} ${buyerSurname}`,
            city: address.city,
            country: 'TR', // ISO 3166-1 alpha-2 format (required by Iyzico)
            address: address.fullAddress,
            zipCode: zipCode, // Iyzico requires this field
          },
          billingAddress: {
            contactName: `${buyerName} ${buyerSurname}`,
            city: address.city,
            country: 'TR', // ISO 3166-1 alpha-2 format (required by Iyzico)
            address: address.fullAddress,
            zipCode: zipCode, // Iyzico requires this field
          },
          saveCard: false,
          addressId: selectedAddressId,
        };

        // Initialize payment with Iyzico
        const paymentResult = await paymentService.initializePayment(paymentData);
        
        // Redirect to Iyzico payment page
        window.location.href = paymentResult.paymentPageUrl;
        
        // Note: After payment, Iyzico will redirect to callback URL
        // which will create the order atomically
        return;
      }

      const orderData: CreateOrderDTO = {
        addressId: selectedAddressId || undefined,
        desiredDeliveryTime: desiredDeliveryTimeIso,
        notes: notes || undefined,
        paymentMethod: 'CASH',
      };

      const order = await orderService.createOrder(orderData);
      await clearCart();
      navigate(`/orders/${order.id}`);
    } catch (err: any) {
      // Extract user-friendly error message from backend response
      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
      
      if (err.response?.data?.error) {
        // Backend returns standardized error format: { success: false, error: { code, message } }
        errorMessage = err.response.data.error.message || errorMessage;
      } else if (err.response?.data?.message) {
        // Fallback for non-standard error responses
        errorMessage = Array.isArray(err.response.data.message)
          ? err.response.data.message.join(', ')
          : err.response.data.message;
      } else if (err.message) {
        // Network errors or other exceptions
        if (err.message.includes('Network Error') || err.message.includes('timeout')) {
          errorMessage = 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.';
        } else {
          errorMessage = err.message;
        }
      }

      // Log technical details for debugging (not shown to user)
      console.error('Payment/Order error:', {
        message: errorMessage,
        status: err.response?.status,
        data: err.response?.data,
        originalError: err,
      });

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">Sepetiniz boş</p>
          <Button onClick={() => navigate('/')}>Alışverişe Başla</Button>
        </div>
      </MainLayout>
    );
  }

  const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Siparişi Tamamla
        </h1>

        {!isAuthenticated && (
          <p className="text-sm text-gray-600 mb-4">
            Giriş yapmadan sipariş verebilirsiniz.{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Giriş yap
            </Link>
          </p>
        )}

        {restaurantOpen === false && (
          <div
            className="mb-6 p-4 rounded-2xl border border-red-200 bg-red-50 text-red-800 text-sm font-medium"
            role="alert"
          >
            Restoran şu anda kapalı. Sipariş veremezsiniz.
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Forms */}
          <div className="lg:col-span-2 space-y-6">
            {!isAuthenticated && (
              <Card>
                <h2 className="text-xl font-bold text-gray-900 mb-4">İletişim Bilgileri</h2>
                <GuestContactForm value={guestContact} onChange={setGuestContact} />
              </Card>
            )}

            {isAuthenticated && (
              <Card>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Teslimat Telefonu</h2>
                <div className="space-y-4">
                  <Input
                    label="Telefon"
                    type="tel"
                    placeholder="05551234567"
                    value={checkoutPhone}
                    onChange={(e) => setCheckoutPhone(e.target.value)}
                    onBlur={() => {
                      if (isValidTurkishMobilePhone(checkoutPhone)) {
                        setCheckoutPhone(formatTurkishMobileForDisplay(checkoutPhone));
                      }
                    }}
                    required
                    helperText="11 haneli cep telefonu (05 ile başlar). Örnek: 05551234567"
                  />
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={savePhoneToProfile}
                      onChange={(e) => setSavePhoneToProfile(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/30"
                    />
                    <span className="text-sm text-gray-700">
                      Bu numarayı profilime kaydet
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Sonraki siparişlerde otomatik doldurulur. Profilinizden istediğiniz zaman güncelleyebilirsiniz.
                      </span>
                    </span>
                  </label>
                </div>
              </Card>
            )}

            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Teslimat Adresi
              </h2>

              {!isAuthenticated ? (
                <GuestDeliveryAddressSection
                  value={guestAddress}
                  onChange={setGuestAddress}
                />
              ) : addresses.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Henüz kayıtlı adresiniz yok. Lütfen yeni bir adres ekleyin.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddressModalOpen(true)}
                    fullWidth
                  >
                    Yeni Adres Ekle
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adres Seçin
                    </label>
                    <select
                      value={selectedAddressId}
                      onChange={async (e) => {
                        const newAddressId = e.target.value;
                        setSelectedAddressId(newAddressId);
                        setAddressZoneWarning(null);
                        
                        // Validate service zone for selected address
                        if (newAddressId && serviceZoneId) {
                          await validateAddressZone(newAddressId);
                        }
                      }}
                      className="w-full px-5 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      required
                    >
                      {addresses.map((addr) => (
                        <option key={addr.id} value={addr.id}>
                          {addr.title} {addr.isDefault && '(Varsayılan)'} - {addr.fullAddress}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedAddress && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-900">{selectedAddress.fullAddress}</p>
                      <p className="text-sm text-gray-600">
                        {selectedAddress.district}, {selectedAddress.city}
                      </p>
                      {selectedAddress.buildingInfo && (
                        <p className="text-sm text-gray-600">{selectedAddress.buildingInfo}</p>
                      )}
                    </div>
                  )}

                  {/* Service Zone Warning */}
                  {addressZoneWarning && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium mb-1">⚠️ Uyarı</p>
                      <p className="text-sm text-yellow-700">{addressZoneWarning}</p>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddressModalOpen(true)}
                    fullWidth
                  >
                    Yeni Adres Ekle
                  </Button>
                </div>
              )}
            </Card>

            {/* Delivery Time */}
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Teslimat Zamanı
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDeliveryMode('now');
                      setDesiredDeliveryTime('');
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      deliveryMode === 'now'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium">Hemen Teslimat</p>
                    <p className="text-sm text-gray-500">Mümkün olan en kısa sürede</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryMode('scheduled')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      deliveryMode === 'scheduled'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium">Saat Belirle</p>
                    <p className="text-sm text-gray-500">İstediğiniz zaman</p>
                  </button>
                </div>

                {deliveryMode === 'scheduled' && (
                  <div>
                    <Input
                      label="Teslimat Tarihi ve Saati"
                      type="datetime-local"
                      value={desiredDeliveryTime}
                      onChange={(e) => setDesiredDeliveryTime(e.target.value)}
                      required
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>
                )}
              </div>
            </Card>

            {/* Payment Method */}
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Ödeme Yöntemi
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('CASH')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      paymentMethod === 'CASH'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium">Kapıda Nakit</p>
                    <p className="text-sm text-gray-500">Teslimatta nakit ödeme</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('CREDIT_CARD')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      paymentMethod === 'CREDIT_CARD'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium">Kredi Kartı</p>
                    <p className="text-sm text-gray-500">Güvenli ödeme</p>
                  </button>
                </div>

                {paymentMethod === 'CREDIT_CARD' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium mb-1">ℹ️ Bilgi</p>
                    <p className="text-sm text-blue-700 mb-3">
                      Kredi kartı ile ödeme için Iyzico güvenli ödeme sayfasına yönlendirileceksiniz.
                      Ödeme işleminiz tamamlandıktan sonra siparişiniz otomatik olarak oluşturulacaktır.
                    </p>
                    <div className="flex items-center gap-3 pt-2 border-t border-blue-200">
                      <div className="bg-white rounded px-2 py-1 flex items-center justify-center shadow-sm">
                        <img src="/logos/visa.png" alt="Visa" className="h-5 object-contain" />
                      </div>
                      <div className="bg-white rounded px-2 py-1 flex items-center justify-center shadow-sm">
                        <img src="/logos/mastercard.png" alt="Mastercard" className="h-5 object-contain" />
                      </div>
                      <div className="bg-white rounded px-2 py-1 flex items-center justify-center shadow-sm">
                        <img src="/logos/iyzico.png" alt="Iyzico ile Öde" className="h-5 object-contain" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Notes */}
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Notlar (Opsiyonel)
              </h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Kurye veya restoran için özel notlar..."
                rows={3}
                className="w-full px-5 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              />
            </Card>
          </div>

          {/* Right Column: Summary */}
          <div className="lg:col-span-1">
            <OrderSummary
              title="Sipariş Özeti"
              items={items.map((item) => ({
                name: item.productName || 'Ürün',
                quantity: item.quantity,
                unitPrice: item.itemTotal ?? 0,
                optionNames: item.optionNames,
                removedIngredientNames: item.removedIngredientNames,
                specialInstructions: item.specialInstructions,
              }))}
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              serviceFee={serviceFee}
              promotionDiscount={promotionDiscount}
              appliedPromotionName={appliedPromotionName}
              total={total}
              submitLabel="Siparişi Onayla"
              isSubmitting={isSubmitting}
              submitDisabled={restaurantOpen === false}
              error={error}
            />
          </div>
        </form>

        {isAuthenticated && (
          <AddAddressModal
            isOpen={isAddressModalOpen}
            onClose={() => setIsAddressModalOpen(false)}
            onSuccess={handleAddressAdded}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default CheckoutPage;
